import {
  EdibleLiquidIcon,
  ExtractIcon,
  Flex,
  FlowerIcon,
  GummyIcon,
  PrerollIcon,
  TinctureIcon,
  TopicalIcon,
  Typography,
  VapeIcon,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import type { WizardStepProps } from '../../../utils/wizard/types';
import { WizardStepKey } from '../../../utils/wizard/types';

import tileStyles from '../wizardOptionTile.module.css';
import stepStyles from '../wizardStep.module.css';
import styles from './categoryStep.module.css';

interface CategoryData {
  defaultLabel: string;
  description: string;
  icon: ReactNode;
  value: string;
}

const CATEGORIES: CategoryData[] = [
  {
    defaultLabel: 'Flower',
    description: 'Classic experience; fast onset when smoked.',
    icon: <FlowerIcon size="md" color="text-main" contrastCompliant />,
    value: 'flower',
  },
  {
    defaultLabel: 'Pre-rolls',
    description: 'Ready-to-smoke flower — easy and no prep.',
    icon: <PrerollIcon size="md" color="text-main" contrastCompliant />,
    value: 'pre-roll',
  },
  {
    defaultLabel: 'Edible solid',
    description: 'Slower onset with longer-lasting effects.',
    icon: <GummyIcon size="md" color="text-main" contrastCompliant />,
    value: 'edible-solid',
  },
  {
    defaultLabel: 'Edible liquid',
    description: 'Slower onset, easy to sip and dose.',
    icon: <EdibleLiquidIcon size="md" color="text-main" contrastCompliant />,
    value: 'edible-liquid',
  },
  {
    defaultLabel: 'Extracts',
    description: 'Potent extracts for experienced users.',
    icon: <ExtractIcon size="md" color="text-main" contrastCompliant />,
    value: 'extract',
  },
  {
    defaultLabel: 'Tinctures',
    description: 'Flexible dosing; can be faster than edibles.',
    icon: <TinctureIcon size="md" color="text-main" contrastCompliant />,
    value: 'tincture',
  },
  {
    defaultLabel: 'Topicals',
    description: 'Applied to the skin for targeted use.',
    icon: <TopicalIcon size="md" color="text-main" contrastCompliant />,
    value: 'topical',
  },
  {
    defaultLabel: 'Vape',
    description: 'Fast onset with smooth, discreet pulls.',
    icon: <VapeIcon size="md" color="text-main" contrastCompliant />,
    value: 'vape',
  },
];

const MAX_SELECTION = 2;

interface CategoryOptionProps {
  description: string;
  disabled: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  selected: boolean;
}

function CategoryOption({
  description,
  disabled,
  icon,
  label,
  onClick,
  selected,
}: CategoryOptionProps) {
  const className = clsx(
    tileStyles['tile'],
    stepStyles['option'],
    styles['option'],
    selected && tileStyles['selected'],
    disabled && tileStyles['disabled']
  );

  return (
    <button
      type="button"
      className={className}
      onClick={disabled ? undefined : onClick}
      aria-pressed={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
    >
      <Flex gap={8} alignItems="center">
        {icon}
        <Typography variant="body-semibold">{label}</Typography>
      </Flex>
      <Typography
        variant="small-body-regular"
        color="grays-dark"
        textAlign="left"
        width="100%"
      >
        {description}
      </Typography>
    </button>
  );
}

interface CategoryStepProps extends WizardStepProps {
  customLabels?: Record<string, string>;
  disabledCategories?: string[];
}

export function CategoryStep({
  customLabels,
  disabledCategories,
  form,
  updateForm,
}: CategoryStepProps) {
  const selections = form[WizardStepKey.Category];
  const isMaxed = selections.length >= MAX_SELECTION;

  const handleToggle = (value: string) => {
    const isSelected = selections.includes(value);

    if (isSelected) {
      updateForm({
        ...form,
        [WizardStepKey.Category]: selections.filter((v) => v !== value),
      });
    } else if (!isMaxed) {
      updateForm({
        ...form,
        [WizardStepKey.Category]: [...selections, value],
      });
    }
  };

  const getLabel = (category: CategoryData): string =>
    customLabels?.[category.value] ?? category.defaultLabel;

  return (
    <Flex flexDirection="column" alignItems="center" gap={32} width="100%">
      <Flex flexDirection="column" alignItems="center" gap={8}>
        <Typography variant="h2-semibold" textAlign="center">
          How do you like to consume?
        </Typography>
        <Typography textAlign="center">Pick up to 2 categories.</Typography>
      </Flex>

      <div className={clsx(stepStyles['grid'], styles['grid'])}>
        {CATEGORIES.map((category) => {
          const selected = selections.includes(category.value);
          const isCategoryDisabled = disabledCategories?.includes(
            category.value
          );
          const disabled = isCategoryDisabled || (!selected && isMaxed);

          return (
            <CategoryOption
              key={category.value}
              description={category.description}
              disabled={disabled}
              icon={category.icon}
              label={getLabel(category)}
              onClick={() => handleToggle(category.value)}
              selected={selected}
            />
          );
        })}
      </div>
    </Flex>
  );
}
